import { Alert, Button } from "@opowiedzo/ui";
import { useCustomMutation, useGetIdentity, useInvalidate, useOne } from "@refinedev/core";
import { useState } from "react";
import { useParams } from "react-router";

import { Badge } from "@/components/Badge";
import { Labeled, TextArea, TextInput } from "@/components/Form";
import { DownloadIcon } from "@/components/Icon";
import {
  BackLink,
  Card,
  CardNote,
  DefinitionList,
  DetailGrid,
  ErrorBox,
  Loading,
  PageHeader,
} from "@/components/Page";
import { Table, Td, Th } from "@/components/Table";
import { API_URL } from "@/lib/http";
import {
  formatDateTime,
  formatMoney,
  orderStatus,
  paymentStatus,
  shipmentKind,
  shipmentStatus,
} from "@/lib/format";
import type { AdminIdentity } from "@/providers/authProvider";

interface OrderItem {
  code: string;
  name: string;
  quantity: number;
  total_gross: number;
}

interface Payment {
  id: string;
  provider: string;
  provider_order_id: string | null;
  amount: number;
  status: string;
  created_at: string;
  confirmed_at: string | null;
}

interface RefundRow {
  id: string;
  amount: number;
  reason: string;
  status: string;
  created_at: string;
}

interface ShipmentRow {
  id: string;
  kind: string;
  status: string;
  tracking_number: string | null;
}

interface TimelineEntry {
  kind: string;
  label: string;
  at: string | null;
  detail: string | null;
  done: boolean;
}

interface Note {
  id: string;
  created_at: string;
  author: string | null;
  text: string;
}

interface OrderDetail extends Record<string, unknown> {
  id: string;
  status: string;
  total_gross: number;
  currency: string;
  payment_method: string | null;
  invoice_no: string | null;
  created_at: string;
  paid_at: string | null;
  buyer_email: string | null;
  buyer_name: string | null;
  tax_no: string | null;
  memoir_title: string | null;
  items: OrderItem[];
  payments: Payment[];
  refunds: RefundRow[];
  shipments: ShipmentRow[];
  certificate_ready: boolean;
  refunded_total: number;
  timeline: TimelineEntry[];
  notes: Note[];
}

export function OrderShowPage() {
  const { id = "" } = useParams();
  const { query } = useOne<OrderDetail>({ resource: "orders", id });
  const { data: identity } = useGetIdentity<AdminIdentity | null>();
  const permissions = identity?.permissions ?? [];
  const order = query.data?.data;

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!order) return null;

  const status = orderStatus(order.status);

  return (
    <>
      <BackLink to="/zamowienia">Zamówienia</BackLink>
      <PageHeader
        title={`Zamówienie z ${formatDateTime(order.created_at)}`}
        description={order.buyer_email ?? undefined}
        actions={<Badge tone={status.tone}>{status.label}</Badge>}
      />

      <DetailGrid
        main={
          <>
            <Card title="Oś czasu">
              <ol className="flex flex-col gap-3.25">
                {order.timeline.map((entry, index) => (
                  <li key={`${entry.kind}-${index}`} className="flex gap-2.5">
                    <span
                      aria-hidden
                      className={`mt-1 size-2.5 shrink-0 rounded-pill ${
                        entry.done ? "bg-accent-alt" : "border border-line bg-bg"
                      }`}
                    />
                    <div>
                      <p className="text-base text-ink">
                        {entry.label}
                        {!entry.done && <span className="text-ink-soft"> - jeszcze nie</span>}
                      </p>
                      <p className="text-sm text-ink-soft">
                        {/* Brak daty to brak daty, a nie kreska sklejona z opisem
                            w "- - czeka na aktywację". */}
                        {[entry.at ? formatDateTime(entry.at) : null, entry.detail]
                          .filter(Boolean)
                          .join(" - ")}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </Card>

            <Card title="Pozycje">
              <Table>
                <thead>
                  <tr>
                    <Th>Produkt</Th>
                    <Th align="right">Ilość</Th>
                    <Th align="right">Kwota</Th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item) => (
                    <tr key={item.code}>
                      <Td>{item.name}</Td>
                      <Td align="right" numeric>
                        {item.quantity}
                      </Td>
                      <Td align="right" numeric>
                        {formatMoney(item.total_gross, order.currency)}
                      </Td>
                    </tr>
                  ))}
                  <tr>
                    <Td>
                      <strong>Razem</strong>
                    </Td>
                    <Td align="right" />
                    <Td align="right" numeric>
                      <strong>{formatMoney(order.total_gross, order.currency)}</strong>
                    </Td>
                  </tr>
                </tbody>
              </Table>
            </Card>

            <Card title="Płatności">
              <Table>
                <thead>
                  <tr>
                    <Th>Dostawca</Th>
                    <Th>Identyfikator</Th>
                    <Th align="right">Kwota</Th>
                    <Th>Status</Th>
                    <Th>Potwierdzona</Th>
                  </tr>
                </thead>
                <tbody>
                  {order.payments.map((payment) => {
                    const stan = paymentStatus(payment.status);
                    return (
                      <tr key={payment.id}>
                        <Td>{payment.provider}</Td>
                        <Td>{payment.provider_order_id ?? "-"}</Td>
                        <Td align="right" numeric nowrap>
                          {formatMoney(payment.amount, order.currency)}
                        </Td>
                        <Td>
                          <Badge tone={stan.tone}>{stan.label}</Badge>
                        </Td>
                        <Td nowrap>{formatDateTime(payment.confirmed_at)}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card>

            {order.shipments.length > 0 && (
              <Card title="Przesyłki">
                <Table>
                  <thead>
                    <tr>
                      <Th>Rodzaj</Th>
                      <Th>Status</Th>
                      <Th>Numer śledzenia</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.shipments.map((shipment) => {
                      const stan = shipmentStatus(shipment.status);
                      return (
                        <tr key={shipment.id}>
                          <Td>{shipmentKind(shipment.kind)}</Td>
                          <Td>
                            <Badge tone={stan.tone}>{stan.label}</Badge>
                          </Td>
                          <Td numeric>{shipment.tracking_number ?? "-"}</Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card>
            )}
          </>
        }
        side={
          <>
            <Card title="Dane">
              <DefinitionList
                rows={[
                  ["Kupujący", order.buyer_name ?? "-"],
                  ["E-mail", order.buyer_email ?? "-"],
                  ["NIP", order.tax_no ?? "-"],
                  ["Metoda płatności", order.payment_method ?? "-"],
                  ["Faktura", order.invoice_no ?? "jeszcze nie wystawiona"],
                  ["Opłacone", formatDateTime(order.paid_at)],
                  ["Opowieść", order.memoir_title ?? "-"],
                  [
                    "Certyfikat",
                    order.certificate_ready ? (
                      <a
                        href={`${API_URL}/orders/${order.id}/certificate`}
                        className="inline-flex items-center gap-1.5 text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        <DownloadIcon />
                        Pobierz PDF
                      </a>
                    ) : (
                      "jeszcze się składa"
                    ),
                  ],
                ]}
              />
            </Card>

            <RefundPanel
              orderId={order.id}
              currency={order.currency}
              totalGross={order.total_gross}
              refundedTotal={order.refunded_total}
              refunds={order.refunds}
              allowed={permissions.includes("orders.refund")}
            />

            <NotesPanel
              orderId={order.id}
              notes={order.notes}
              allowed={permissions.includes("orders.note")}
            />
          </>
        }
      />
    </>
  );
}

function RefundPanel({
  orderId,
  currency,
  totalGross,
  refundedTotal,
  refunds,
  allowed,
}: {
  orderId: string;
  currency: string;
  totalGross: number;
  refundedTotal: number;
  refunds: RefundRow[];
  allowed: boolean;
}) {
  const invalidate = useInvalidate();
  const { mutate, mutation } = useCustomMutation();
  // Kwotę wpisuje człowiek w złotówkach, do API idzie w groszach.
  const [zlote, setZlote] = useState("");
  const [powod, setPowod] = useState("");

  const grosze = Math.round(Number(zlote.replace(",", ".")) * 100);
  const poprawna = Number.isFinite(grosze) && grosze > 0 && grosze + refundedTotal <= totalGross;

  return (
    <Card title="Zwrot">
      {refunds.length > 0 && (
        <ul className="mb-3.5 flex flex-col gap-2 text-base">
          {refunds.map((refund) => (
            <li key={refund.id} className="text-ink-soft">
              <span className="tabular text-ink">{formatMoney(refund.amount, currency)}</span> -{" "}
              {refund.status} - {refund.reason}
            </li>
          ))}
        </ul>
      )}

      {!allowed ? (
        <p className="text-base text-ink-soft">
          Zwroty wykonuje superadmin. Zgłoś prośbę razem z powodem - trafi ona do rejestru.
        </p>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            mutate(
              {
                url: `/orders/${orderId}/refund`,
                method: "post",
                values: { amount: grosze, reason: powod },
                errorNotification: false,
                successNotification: false,
              },
              {
                onSuccess: () => {
                  setZlote("");
                  setPowod("");
                  invalidate({ resource: "orders", invalidates: ["detail"], id: orderId });
                },
              },
            );
          }}
        >
          <Labeled label="Kwota w złotych">
            <TextInput
              inputMode="decimal"
              required
              value={zlote}
              onChange={(event) => setZlote(event.target.value)}
            />
          </Labeled>
          <Labeled label="Powód">
            <TextArea
              required
              rows={3}
              value={powod}
              onChange={(event) => setPowod(event.target.value)}
            />
          </Labeled>

          {zlote !== "" && !poprawna && (
            <Alert tone="error">
              Kwota musi być dodatnia i nie większa niż to, co zostało wpłacone.
            </Alert>
          )}
          {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}

          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending || !poprawna || !powod}
          >
            {mutation.isPending ? "Zwracam..." : "Zwróć pieniądze"}
          </Button>
          <CardNote>Zwrot trafia do rejestru audytu razem z powodem.</CardNote>
        </form>
      )}
    </Card>
  );
}

function NotesPanel({
  orderId,
  notes,
  allowed,
}: {
  orderId: string;
  notes: Note[];
  allowed: boolean;
}) {
  const invalidate = useInvalidate();
  const { mutate, mutation } = useCustomMutation();
  const [tekst, setTekst] = useState("");

  return (
    <Card title="Notatki">
      {notes.length === 0 ? (
        <p className="mb-3.5 text-base text-ink-soft">Jeszcze nikt nic tu nie zapisał.</p>
      ) : (
        <ul className="mb-3.5 flex flex-col gap-2.5">
          {notes.map((note) => (
            <li key={note.id}>
              <p className="text-base text-ink">{note.text}</p>
              <p className="text-sm text-ink-soft">
                {note.author ?? "nieznany"} - {formatDateTime(note.created_at)}
              </p>
            </li>
          ))}
        </ul>
      )}

      {allowed && (
        <form
          className="flex flex-col gap-2.5"
          onSubmit={(event) => {
            event.preventDefault();
            mutate(
              {
                url: `/orders/${orderId}/notes`,
                method: "post",
                values: { text: tekst },
                errorNotification: false,
                successNotification: false,
              },
              {
                onSuccess: () => {
                  setTekst("");
                  invalidate({ resource: "orders", invalidates: ["detail"], id: orderId });
                },
              },
            );
          }}
        >
          <TextArea
            rows={3}
            required
            value={tekst}
            onChange={(event) => setTekst(event.target.value)}
            placeholder="Co ustaliliśmy z klientem?"
          />
          {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}
          <Button
            className="self-start"
            type="submit"
            disabled={mutation.isPending || tekst.trim() === ""}
          >
            Dopisz notatkę
          </Button>
        </form>
      )}
    </Card>
  );
}
